import { createApp, configureCompat } from 'vue'
import App from './App.vue'
import ElementPlus from 'element-plus'
import router from './router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import 'element-plus/dist/index.css'
import './assets/admin-theme.css'
import './assets/robocoin-theme.css'
import store from './store'
import { installI18n } from './utils/i18n'
if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_ENABLE_MOCKS === 'true') {
  require('../src/api/mock')
}
import { getAdminToken, getUserToken, logout, logoutAdmin } from './utils/auth'
import { http, installHttpInterceptors } from './api/http'

configureCompat({
  MODE: 2,
  GLOBAL_MOUNT: 'suppress-warning',
  GLOBAL_EXTEND: 'suppress-warning',
  GLOBAL_PROTOTYPE: 'suppress-warning',
  GLOBAL_SET: 'suppress-warning',
  GLOBAL_DELETE: 'suppress-warning',
  INSTANCE_SET: 'suppress-warning',
  INSTANCE_DELETE: 'suppress-warning',
  OPTIONS_BEFORE_DESTROY: 'suppress-warning',
  OPTIONS_DESTROYED: 'suppress-warning',
  INSTANCE_EVENT_EMITTER: 'suppress-warning',
  INSTANCE_EVENT_HOOKS: 'suppress-warning',
  INSTANCE_LISTENERS: 'suppress-warning',
  COMPONENT_ASYNC: false,
  TRANSITION_GROUP_ROOT: false,
  ATTR_FALSE_VALUE: 'suppress-warning',
  COMPILER_V_ON_NATIVE: 'suppress-warning',
  COMPILER_V_BIND_SYNC: 'suppress-warning',
  COMPILER_V_BIND_OBJECT_ORDER: 'suppress-warning',
  COMPILER_V_IF_V_FOR_PRECEDENCE: 'suppress-warning',
  COMPILER_NATIVE_TEMPLATE: 'suppress-warning',
  COMPILER_FILTERS: 'suppress-warning',
  COMPONENT_V_MODEL: false
})

installHttpInterceptors(axios)

const publicRoutes = ['home', 'login', 'adminLogin', 'mobilePortal', 'mobileTaskPublish', 'openApiDoc']

router.beforeEach((to, from, next) => {
  const userToken = getUserToken()
  const adminToken = getAdminToken()
  const requiresAdmin = to.matched.some(record => record.meta && record.meta.requiresAdmin)
  const requiresUser = to.matched.some(record => record.meta && record.meta.requiresUser)

  if (publicRoutes.includes(to.name)) {
    if (userToken && to.name === 'login') {
      next({ name: 'home' })
      return
    }
    if (adminToken && to.name === 'adminLogin') {
      next({ name: 'adminTaskMarket' })
      return
    }
    next()
    return
  }

  if (requiresAdmin && !adminToken) {
    next({ name: 'adminLogin', query: { redirect: to.name || 'adminTaskMarket' } })
    return
  }

  if (requiresUser && !userToken) {
    next({ name: 'login', query: { redirect: to.name || 'home' } })
    return
  }

  if (!userToken && !adminToken) {
    next({ name: 'login', query: { redirect: to.name || 'home' } })
    return
  }

  next()
})

router.afterEach(to => {
  const isAdminPage = to.matched.some(record => record.meta && record.meta.requiresAdmin)
  document.body.classList.toggle('admin-mode', isAdminPage)
})

const app = createApp(App)
let lastRuntimeErrorAt = 0
const notifyRuntimeError = () => {
  const now = Date.now()
  if (now - lastRuntimeErrorAt < 3000) return
  lastRuntimeErrorAt = now
  ElMessage.error('An unexpected page error occurred. Refresh the page and try again.')
}

app.config.errorHandler = (error, instance, info) => {
  console.error('[RoboCoin UI error]', info, error)
  notifyRuntimeError()
}

window.addEventListener('unhandledrejection', event => {
  console.error('[RoboCoin unhandled request]', event.reason)
  notifyRuntimeError()
})

window.addEventListener('roc:user-session-expired', event => {
  if (!getUserToken()) return
  logout()
  const replaced = event && event.detail && event.detail.reason === 'SESSION_REPLACED'
  ElMessage.warning(replaced ? 'Your account signed in elsewhere. You have been signed out.' : 'Your login session has expired. Please sign in again.')
  if (router.currentRoute.value.name !== 'login') {
    router.push({ name: 'login', query: { redirect: router.currentRoute.value.name || 'home' } })
  }
})

window.addEventListener('roc:admin-session-expired', event => {
  if (!getAdminToken()) return
  logoutAdmin()
  const replaced = event && event.detail && event.detail.reason === 'SESSION_REPLACED'
  ElMessage.warning(replaced ? 'This administrator account signed in elsewhere. You have been signed out.' : 'Your administrator session has expired. Please sign in again.')
  if (router.currentRoute.value.name !== 'adminLogin') {
    router.push({ name: 'adminLogin', query: { redirect: router.currentRoute.value.name || 'adminTaskMarket' } })
  }
})

app.config.globalProperties.$http = http
installI18n(app)
app.use(ElementPlus)
app.use(router)
app.use(store)
app.mount('#app')
